import { action, computed, flow, makeObservable, observable } from 'mobx';
import { GlobalStore, GridStore } from '../../../stores';
import { ApiResponse, FormStore } from '@roc/feature-app-core';
import { LenderFees } from '../types/companyProfileTypes';
import { ExternalLenderStore } from './externalLenderStore';
import { LenderTermTierEconomicsService } from '../services/lenderTermTierEconomicsService';

const REQUIRED_MESSAGE = 'This field is required';

const form = {
  fields: {
    processingFees: {
      value: '',
      error: null,
      rule: 'required',
      message: REQUIRED_MESSAGE,
    },
    underwriterFees: {
      value: '',
      error: null,
      rule: 'required',
      message: REQUIRED_MESSAGE,
    },
    adminFees: {
      value: '',
      error: null,
      rule: 'required',
      message: REQUIRED_MESSAGE,
    },
    commitmentFees: {
      value: '',
      error: null,
      rule: 'required',
      message: REQUIRED_MESSAGE,
    },
    buydownFees: {
      value: '',
      error: null,
      rule: 'required',
      message: REQUIRED_MESSAGE,
    },
    termOriginationPoints: {
      value: '',
      error: null,
      rule: 'required',
      message: REQUIRED_MESSAGE,
    },
    yieldSpreadPremium: {
      value: '',
      error: null,
      rule: 'required',
      message: REQUIRED_MESSAGE,
    },
    isDefaultTier: {
      value: false,
      error: null,
      rule: 'required',
      message: 'This value is required',
    },
    disablePricing: {
      value: false,
      error: null,
      rule: 'required',
      message: 'This value is required',
    },
    newEconomicTier: {
      value: true,
      error: null,
      rule: 'required',
      message: 'This value is required',
    },
    existingTermTier: {
      value: '',
      error: null,
      rule: [{ required_if: ['newEconomicTier', false] }],
      message: 'This value is required',
    },
    createNewTermTier: {
      value: '',
      error: null,
      rule: [{ required_if: ['newEconomicTier', true] }],
      message: 'This value is required',
    },
  },
  meta: {
    isValid: false,
    error: null,
  },
};

export class LenderTermTierEconomicsFormStore extends FormStore {
  private globalStore: GlobalStore;
  salesforceLenderStore: ExternalLenderStore;
  public possibleTierBorrowers: GridStore;
  lenderTermTierEconomicsService: LenderTermTierEconomicsService;

  borrowerList: any[]
  termTierList: any[]
  selectTierOptions: any[]
  termTierToDelete: Number;
  borrowerToDelete: Number;
  isEditing = false;
  getAllBorrowers = true;
  lenderFees: LenderFees;

  constructor(globalStore, lenderStore) {
    super(form, globalStore);
    this.lenderTermTierEconomicsService = new LenderTermTierEconomicsService()
    this.possibleTierBorrowers = new GridStore(() => this.getAllTermTierUsers());
    this.possibleTierBorrowers.showFilters = false;
    this.borrowerList = []
    this.termTierList = []
    this.globalStore = globalStore;
    this.salesforceLenderStore = lenderStore;

    makeObservable(this, {
      isEditing: observable,
      lenderFees: observable,
      termTierList: observable,
      borrowerList: observable,
      termTierToDelete: observable,
      getAllBorrowers: observable,
      selectTierOptions: observable,
      borrowerToDelete: observable,
      addBorrower: action,
      resetBorrowers: action,
      deleteBorrower: action,
      setTermTierToDelete: action,
      setGetAllBorrowers: action,
      setBorrowerToDelete: action,
      loadFromStore: action,
      saveTermTier: flow,
      getAllTermTiers: flow,
      moveBorrowerToNewTermTier: flow,
      updateTermTierValues: flow,
      deleteTermTier: flow,
      addRemoveBorrowerFromTier: flow,
      deleteBorrowerFromTier: flow,
      getNewEconomicTierValue: computed,
      getSelectedTierId: computed,
      oldBorrowerIdList: computed
    });
  }

  *saveTermTier() {
    try {
      this.runFormValidation();
      if (this.form.meta.isValid) {
        this.isEditing = false;
        const formValues = this.getFormValues();

        const data = {
          submissionPricingTiers: {
            tierId: formValues.existingTermTier,
            lenderId: this.globalStore.lenderInfo.lenderId,
            name: formValues.createNewTermTier,
            yieldSpreadPremium: formValues.yieldSpreadPremium,
            termOriginationPoints: formValues.termOriginationPoints,
            processingFees: formValues.processingFees,
            adminFees: formValues.adminFees,
            underwriterFees: formValues.underwriterFees,
            commitmentFees: formValues.commitmentFees,
            buydownFees: formValues.buydownFees,
            isDefaultTier: formValues.isDefaultTier,
            disablePricing: formValues.disablePricing,
          },
          borrowerIds: this.borrowerList
        }

        if (formValues.newEconomicTier) {
          yield this.lenderTermTierEconomicsService.saveTermTier(
            data
          );
        } else {
          this.addRemoveBorrowerFromTier()
          yield this.lenderTermTierEconomicsService.updateTermTier(
            data
          );

        }

        this.reset()
        this.borrowerList = []
        this.getAllTermTiers()
        this.globalStore.notificationStore.showSuccessNotification({
          message: 'Tier have been saved successfully.',
        });
      }
    } catch (error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while saving company fees',
      });
    }
  }

  protected async getAllTermTierUsers() {
    try {
      if (this.getAllBorrowers) {
        const response: ApiResponse = await this.lenderTermTierEconomicsService.getAllTermTierUsers(
          this.possibleTierBorrowers.gridData.meta.pageNumber,
          this.possibleTierBorrowers.gridData.meta.pageSize,
          this.possibleTierBorrowers.gridData.meta.sortBy,
          this.possibleTierBorrowers.gridData.meta.sortDir,
          this.possibleTierBorrowers.gridData.meta.filters,
          this.possibleTierBorrowers.gridData.meta.dropdownFilters,
        );
        return response;
      } else {
        const response: ApiResponse = await this.lenderTermTierEconomicsService.getAllPossibleOrMatchingTierBorrowers(
          this.possibleTierBorrowers.gridData.meta.pageNumber,
          this.possibleTierBorrowers.gridData.meta.pageSize,
          this.possibleTierBorrowers.gridData.meta.sortBy,
          this.possibleTierBorrowers.gridData.meta.sortDir,
          this.possibleTierBorrowers.gridData.meta.filters,
          this.possibleTierBorrowers.gridData.meta.dropdownFilters,
          this.termTierToDelete
        );
        return response;
      }

    }
    catch (error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while fetching Term Tier users'
      });
    }
  }

  addBorrower(borrower) {
    this.borrowerList = [...this.borrowerList, borrower.borrowerId];
  }

  deleteBorrower(borrowerId) {
    this.borrowerList = this.borrowerList.filter(b => b !== borrowerId);
  }

  resetBorrowers() {
    this.borrowerList = []
  }

  setTermTierToDelete(termTierId) {
    this.termTierToDelete = termTierId;
  }

  setBorrowerToDelete(borrowerId) {
    this.borrowerToDelete = borrowerId;
  }

  *getAllTermTiers() {
    try {
      const response = yield this.lenderTermTierEconomicsService.getAllTermTiers(
        this.globalStore.lenderInfo.lenderId
      );
      this.termTierList = response.data.data

      const selectOptions = this.termTierList.map(tier => ({
        label: tier.submissionPricingTiers.name,
        value: tier.submissionPricingTiers.tierId,
      }));

      this.selectTierOptions = selectOptions
    } catch (error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error Getting Tier Term Economics',
      });
    }
  }

  *deleteTermTier() {
    try {
      yield this.lenderTermTierEconomicsService.deleteTermTier(this.termTierToDelete);
      this.getAllTermTiers()
      this.globalStore.notificationStore.showSuccessNotification({
        message: 'Tier have been deleted successfully.',
      });
    } catch (error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error Deleting Tier Term Economics',
      });
    }
  }

  *deleteBorrowerFromTier() {
    try {
      yield this.lenderTermTierEconomicsService.deleteBorrowerFromTier(this.termTierToDelete, this.borrowerToDelete);
      this.getAllTermTiers()
      this.globalStore.notificationStore.showSuccessNotification({
        message: 'Borrower have been deleted successfully.',
      });
    } catch (error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error Deleting Tier Term Economics',
      });
    }
  }

  *moveBorrowerToNewTermTier() {
    this.runFormValidation();
    const formValues = this.getFormValues();
    if (this.form.fields.existingTermTier.error === false) {
      yield this.lenderTermTierEconomicsService.moveBorrowerToNewTermTier(this.termTierToDelete, this.borrowerToDelete, formValues.existingTermTier);
      this.reset()
      this.getAllTermTiers();
      this.globalStore.notificationStore.showSuccessNotification({
        message: 'Borrower have been moved successfully.',
      });
    }
  }

  loadFromStore(tierId) {
    const tier = this.termTierList.find(
      t => t.submissionPricingTiers.tierId === tierId
    );
    const formObject = {
      processingFees: tier.submissionPricingTiers.processingFees,
      underwriterFees: tier.submissionPricingTiers.underwriterFees,
      adminFees: tier.submissionPricingTiers.adminFees,
      commitmentFees: tier.submissionPricingTiers.commitmentFees,
      buydownFees: tier.submissionPricingTiers.buydownFees,
      termOriginationPoints: tier.submissionPricingTiers.termOriginationPoints,
      yieldSpreadPremium: tier.submissionPricingTiers.yieldSpreadPremium,
      createNewTermTier: tier.submissionPricingTiers.name,
      isDefaultTier: tier.submissionPricingTiers.isDefaultTier,
      disablePricing: tier.submissionPricingTiers.disablePricing,
    };
    this.loadForm(formObject);
  }

  get getNewEconomicTierValue() {
    return this.form.fields.newEconomicTier.value;
  }

  get getSelectedTierId() {
    return this.form.fields.existingTermTier.value;
  }

  setGetAllBorrowers(value: boolean) {
    this.getAllBorrowers = value
  }

  get oldBorrowerIdList() {
    const matchingTier = this.termTierList.find(
      (tier) => tier.submissionPricingTiers.tierId === this.termTierToDelete
    );
    const borrowerIds = matchingTier.lenderBorrowers.map(b => b.borrowerId);
    return borrowerIds
  }

  *addRemoveBorrowerFromTier() {
    try {
      const data = {
        newBorrowersList: this.borrowerList,
        oldBorrowersList: this.oldBorrowerIdList,
        tierId: this.termTierToDelete
      }
      yield this.lenderTermTierEconomicsService.addRemoveBorrowers(data);
      this.getAllTermTiers()
      this.globalStore.notificationStore.showSuccessNotification({
        message: 'Borrowers have been moved successfully.',
      });
    } catch (error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error Moving Borrowers',
      });
    }
  }

  *updateTermTierValues() {
    try {
      this.runFormValidationWithMessage();
      if (this.form.meta.isValid) {
        const formValues = this.getFormValues();
        const data = {
          submissionPricingTiers: {
            tierId: this.termTierToDelete,
            lenderId: this.globalStore.lenderInfo.lenderId,
            name: formValues.createNewTermTier,
            yieldSpreadPremium: formValues.yieldSpreadPremium,
            termOriginationPoints: formValues.termOriginationPoints,
            processingFees: formValues.processingFees,
            adminFees: formValues.adminFees,
            underwriterFees: formValues.underwriterFees,
            commitmentFees: formValues.commitmentFees,
            buydownFees: formValues.buydownFees,
            isDefaultTier: formValues.isDefaultTier,
            disablePricing: formValues.disablePricing,
          },
          borrowerIds: []
        }
        yield this.lenderTermTierEconomicsService.updateTermTier(data);
        this.getAllTermTiers()
        this.globalStore.notificationStore.showSuccessNotification({
          message: 'Tier have been saved successfully.',
        });
      }
    } catch (error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error saving tier information',
      });
    }
  }

  validateTermTierByStep(step?: number) {
    let excludedFields = [];
    if (step === 0) {
      excludedFields = ['yieldSpreadPremium', 'termOriginationPoints', 'commitmentFees', 'adminFees', 'underwriterFees', 'processingFees', 'buydownFees'];
    }
    this.runFormValidation(excludedFields);
    const isValid = this.form.meta.isValid;
    if (!isValid) {
      this.globalStore.notificationStore.showWarningNotification({
        message: 'Please complete all the required fields',
      });
      return false;
    }
    return isValid;
  }

}
